import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { ShieldCheck } from "lucide-react";

/**
 * First-login forced password change for newly approved fixers.
 * After success, refreshes auth state and redirects to /verksted-dashbord.
 */
export default function ForceChangePassword() {
  const { user, setUser } = useAuth();
  const nav = useNavigate();
  const [pw, setPw] = useState({
    current_password: "",
    new_password: "",
    confirm: "",
  });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.new_password !== pw.confirm) {
      toast.error("Nytt passord stemmer ikke overens");
      return;
    }
    if (pw.new_password.length < 8) {
      toast.error("Passordet må være minst 8 tegn");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/change-password", {
        current_password: pw.current_password,
        new_password: pw.new_password,
      });
      toast.success("Passord oppdatert");
      if (user) setUser({ ...user, must_change_password: false });
      // Send to relevant landing based on role
      if (user?.role === "fixer") nav("/verksted-dashbord");
      else if (user?.role === "admin") nav("/admin");
      else nav("/mine-bestillinger");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="tk-container py-20 max-w-md" data-testid="force-change-pw-page">
      <div className="w-12 h-12 rounded-md bg-[#0B1C30] flex items-center justify-center mb-6">
        <ShieldCheck className="text-white" size={22} />
      </div>
      <p className="tk-overline mb-3">Velkommen</p>
      <h1 className="text-3xl font-semibold mb-2">Sett et personlig passord</h1>
      <p className="text-sm text-slate-500 mb-8 leading-relaxed">
        For å sikre kontoen din må du bytte det midlertidige passordet før du får tilgang
        til Tekkdeler-portalen.
      </p>
      <form onSubmit={submit} className="space-y-4">
        <div>
          <label className="text-sm font-medium text-slate-700">Midlertidig passord</label>
          <input
            type="password"
            required
            className="tk-input mt-1"
            value={pw.current_password}
            onChange={(e) => setPw({ ...pw, current_password: e.target.value })}
            data-testid="force-current-pw"
          />
        </div>
        <div>
          <label className="text-sm font-medium text-slate-700">Nytt passord</label>
          <input
            type="password"
            required
            minLength={8}
            className="tk-input mt-1"
            value={pw.new_password}
            onChange={(e) => setPw({ ...pw, new_password: e.target.value })}
            data-testid="force-new-pw"
          />
        </div>
        <div>
          <label className="text-sm font-medium text-slate-700">Bekreft nytt passord</label>
          <input
            type="password"
            required
            className="tk-input mt-1"
            value={pw.confirm}
            onChange={(e) => setPw({ ...pw, confirm: e.target.value })}
            data-testid="force-confirm-pw"
          />
        </div>
        <button
          type="submit"
          disabled={busy}
          className="tk-btn-primary w-full justify-center"
          data-testid="force-submit"
        >
          {busy ? "Lagrer …" : "Sett nytt passord og fortsett"}
        </button>
      </form>
    </div>
  );
}
